// src/useFirebaseState.js
import { REGION_FRANCFORT } from "./regionsFonctions";
import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { mesurerEcriture } from "./mesureEcritures";
import { toast } from "./toasts";
import { logErreurFront } from "./erreursFront";
import { doc, collection, onSnapshot, setDoc, deleteDoc, getDoc, query, where } from "firebase/firestore";
import { db, auth } from "./firebase";
import { onAuthStateChanged } from "firebase/auth";
import { preserverAutorite, identiteInchangee, assistantesInchangees, identiteAdminChange, MAX_ASSISTANTES } from "./champsAutorite";
import { miseEnFormeSaisie } from "./nomsPersonnes";
import { planRetraitRoleAdmin, entreeJournalRetrait, changeQualiteAdmin, sessionSuperAdmin, refusQualiteAdmin, refusIdentiteAdmin } from "./retraitRoleAdmin";
import { fusionnerRoles } from "./rolesHabilitations";
import { calculerDiff, appliquerDiff, egal } from "./seanceDiff";
import { animer } from "./seanceServeur";
import { fusionnerAvecBase } from "./propositionsProjet";
import { projetEcrire, reunionEcrire } from "./ecrituresActivite";

// ── Références Firestore — Agora (vote) ──────────────────────────────────────
export const SESSION_DOC = doc(db, "sessions", "current");
const CONFIG_DOC   = doc(db, "config",   "settings");
const ADMINS_COL   = collection(db, "admins");
const LISTS_COL    = collection(db, "memberLists");
const AUDIT_COL    = collection(db, "auditLog");
const IDEAS_COL    = collection(db, "ideaBox");

// ── Références Firestore — Athos (suivi de projet) ───────────────────────────
const PROJECTS_COL  = collection(db, "projects");
const MEETINGS_COL  = collection(db, "meetings");
const MEMBERS_COL   = collection(db, "members");

// ── Utilitaires crypto ────────────────────────────────────────────────────────
/** Normalise un email de façon robuste :
 *  - NFC (compose les accents décomposés — évite que "é" != "e\u0301")
 *  - lowercase
 *  - trim (espaces début/fin)
 *  - retire tous les whitespace internes (espaces insécables, tab, etc.)
 */
export function normalizeEmail(email) {
  if (!email) return "";
  return String(email)
    .normalize("NFC")
    .toLowerCase()
    .replace(/\s+/g, "")
    .trim();
}

/** Génère un sel aléatoire de 16 bytes en hex. */
export function generateSalt() {
  const array = new Uint8Array(16);
  crypto.getRandomValues(array);
  return Array.from(array).map(b => b.toString(16).padStart(2, "0")).join("");
}

/** Normalise un mot de passe pour éviter les variations Unicode invisibles.
 *  - NFKC : convertit les formes compatibles (ex : é et é, espaces différents)
 *  - trim : retire espaces en début/fin
 *  - remplace les espaces insécables (\u00A0) et zero-width (\u200B, \uFEFF) qui
 *    peuvent s'immiscer via copier-coller depuis gestionnaire de mots de passe / email.
 */
function normalizePassword(plain) {
  if (!plain) return "";
  return plain
    .normalize("NFKC")
    .replace(/[\u00A0\u200B-\u200D\uFEFF]/g, "")
    .trim();
}

/** Hash SHA-256 avec sel optionnel. Compatible legacy (sans sel) et salé. */
export async function hashCode(plain, salt) {
  const encoder = new TextEncoder();
  const norm = normalizePassword(plain);
  const input = salt ? salt + norm : norm;
  const hashBuffer = await crypto.subtle.digest("SHA-256", encoder.encode(input));
  const hashArray  = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map(b => b.toString(16).padStart(2, "0")).join("");
}

/** Helper : calcule un hash SHA-256 hex pour une chaîne quelconque. */
async function sha256Hex(str) {
  const encoder = new TextEncoder();
  const buf = await crypto.subtle.digest("SHA-256", encoder.encode(str));
  return Array.from(new Uint8Array(buf)).map(b => b.toString(16).padStart(2, "0")).join("");
}

/** Vérifie un mot de passe contre un hash, avec sel optionnel.
 *  Essaie plusieurs variantes pour couvrir tous les cas historiques :
 *   1. Normalisé NFKC + trim + retrait caractères invisibles (nouveau standard)
 *   2. Trim seul (avant fix Unicode)
 *   3. Pas de transformation (au cas où l'inscription n'a pas trim)
 *   4. Si un sel est fourni mais que ça échoue, essai sans sel (au cas où le hash stocké est legacy)
 *   5. Si pas de sel fourni mais hash long, essai avec sel par défaut
 */
export async function verifyCode(plain, hash, salt) {
  if (!plain || !hash) return false;
  const safePlain = String(plain);

  // Génère toutes les variantes de normalisation possibles
  const variants = [
    safePlain.normalize("NFKC").replace(/[\u00A0\u200B-\u200D\uFEFF]/g, "").trim(), // 1. Full normalize
    safePlain.trim(),                                                                // 2. Trim seul
    safePlain,                                                                        // 3. Sans transformation
    safePlain.normalize("NFC").trim(),                                                // 4. NFC au lieu de NFKC
  ];

  // Cas avec sel : essai toutes les variantes avec le sel
  if (salt) {
    for (const v of variants) {
      if ((await sha256Hex(salt + v)) === hash) return true;
    }
    // Fallback : le hash a peut-être été créé sans sel à l'origine (import legacy)
    for (const v of variants) {
      if ((await sha256Hex(v)) === hash) return true;
    }
  } else {
    // Cas sans sel : essai toutes les variantes sans sel
    for (const v of variants) {
      if ((await sha256Hex(v)) === hash) return true;
    }
  }

  // Diagnostic : logger en console (invisible en prod sauf pour dev)
  if (typeof window !== "undefined" && window.__DEBUG_LOGIN__) {
    console.warn("[verifyCode] Échec après", variants.length * 2, "tentatives.", {
      hashPrefix: hash.slice(0, 8) + "...",
      hasSalt: !!salt,
      saltPrefix: salt ? salt.slice(0, 8) + "..." : null,
      plainLen: safePlain.length,
    });
  }
  return false;
}

// Génère un identifiant aléatoire court (7 caractères)
export function newId() {
  return Math.random().toString(36).slice(2, 9);
}

// ── AGORA — État de session ───────────────────────────────────────────────────
const initState = () => ({
  session: null, participants: [], agenda: [], votesByScrutin: {}, history: [],
});

// ── C6 (29/09/2026) : une écriture refusée n'efface plus l'application ──────
// Avant : un seul setDoc refusé (document proche de 1 Mio, droit refusé,
// compte invité) posait `error`, et App remplaçait TOUTE l'application par
// l'écran « Erreur de connexion Firebase » : console, écran de salle et
// participants blanchis en pleine séance. Désormais :
//   • l'écran d'erreur reste réservé à l'échec de la LECTURE initiale (sans
//     données, il n'y a rien à montrer), sauf un REFUS de lecture : depuis le
//     lot 4 des règles, la séance ne se lit qu'avec une session Aramis ;
//     sans elle, l'état reste vide (l'écran de salle et le votant lisent la
//     lecture publique, src/seanceServeur.js) ;
//   • une écoute coupée APRÈS réception garde l'affichage et le dit, par un
//     toast qui reste jusqu'à ce qu'on le ferme. Firestore ne relance pas une
//     écoute tombée : l'état affiché est FIGÉ. Toute écriture est donc
//     refusée, et dite, jusqu'au rechargement ;
//   • un refus d'écriture rend `false`, remonte au journal erreursFront (code
//     et motif, jamais le contenu du document) et prévient À CHAQUE FOIS par
//     un toast ;
//   • un acte peut passer son message (`{ messageEchec }`). Avec
//     `{ durable: true }` (console : votes, présence), l'avis reste jusqu'à ce
//     qu'on le ferme, remplacé au prochain échec du MÊME message et retiré
//     quand l'acte réussit ; un autre acte ne l'efface pas ;
//   • les enregistrements AUTOMATIQUES (`{ avisGroupe: true }` : cadre de la
//     séance, repas) partent toujours, mais leur avis est groupé : un par
//     rafale de 15 s ;
//   • les écritures d'ARRIÈRE-PLAN (`{ arrierePlan: true }` : vidage
//     différé de l'ODJ, synchronisation automatique, raccrochage Outlook,
//     réconciliation des pouvoirs) sont groupées de même, et ne repartent PAS
//     pendant la rafale.
//
// ── Lot 4 des règles (18/10/2026) : la séance s'écrit par le SERVEUR ────────
// saveState n'écrit plus sessions/current : il calcule la DIFFÉRENCE entre ce
// que l'écran affichait et ce qu'il veut écrire (src/seanceDiff.js) et
// l'envoie à la fonction seanceAnimer (administrateur, jeton Aramis vérifié),
// qui l'applique en transaction au document tel qu'il est. Un vote ou un
// émargement arrivé entre-temps n'est plus effacé par la console. La
// différence s'affiche AUSSITÔT (elle est rejouée sur chaque instantané reçu
// tant que le serveur ne l'a pas acquise) ; refusée, elle disparaît.
export const RAFALE_ECHEC_MS = 15000;
const MSG_ECHEC_ECRITURE = "La dernière modification de la séance n'a pas été enregistrée. Recommencez ; si le refus se répète, rechargez la page.";
const MSG_ECOUTE_COUPEE = "Connexion à la séance interrompue : les données affichées peuvent être anciennes. Rechargez la page.";
const MSG_ECRITURE_ECOUTE_COUPEE = "Connexion à la séance interrompue : rien n'a été enregistré. Rechargez la page avant toute modification.";
const MSG_INVITE = "Compte invité : consultation seule, rien n'a été enregistré.";
// Une différence acquise par le serveur reste appliquée à l'affichage jusqu'à
// ce qu'un instantané la contienne (au plus ce délai).
const GARDE_ACQUIS_MS = 10000;
// Motif lisible, en petit sous le message : un libellé français, jamais le
// code anglais brut (il reste au journal).
const MOTIFS = {
  "permission-denied": "droit d'écriture refusé", unauthenticated: "connexion à renouveler",
  unavailable: "serveur injoignable", "deadline-exceeded": "serveur trop lent à répondre",
  "resource-exhausted": "trop de modifications à la fois",
};
const codeDe = (err) => String((err && err.code) || "").replace(/^functions\//, "");
const raisonEchec = (err) => (/maximum allowed size|trop volumineuse/i.test(String((err && err.message) || err))
  ? "séance trop volumineuse" : MOTIFS[codeDe(err)] || "refus du serveur");
const avisSeance = (message, err) => <>{message}<br/><small>Motif : {raisonEchec(err)}</small></>;
// Chiffres neutralisés : la taille du document change à chaque écriture, et
// l'empreinte du journal (dédoublonnage, plafond de 10) doit rester la même.
const journaliserSeance = (quoi, err) => logErreurFront({
  message: `${quoi} (sessions/current) : ${codeDe(err) || "sans code"} ${String((err && err.message) || err).slice(0, 200).replace(/\d[\d,. ]*/g, "#")}`,
  source: "seance", composant: "useFirebaseState",
});

/** Rappelle `rappel(connecte)` à chaque changement de session Firebase. Sans
 *  module d'authentification (bancs d'essai), la séance est lue d'emblée. */
function suivreSessionFirebase(rappel) {
  if (!auth) { rappel(true); return () => {}; }
  return onAuthStateChanged(auth, (u) => rappel(!!u));
}

export function useFirebaseState() {
  const [appState, setAppState] = useState(initState());
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState(null);
  const dernierAvis = useRef(0);        // dernier avis d'échec montré
  const echecFond = useRef(0);          // dernier refus d'une écriture d'arrière-plan
  const avisDurables = useRef(new Map()); // message → id du toast durable affiché
  const ecouteCoupee = useRef(false);
  const serveur = useRef(initState());  // dernier instantané reçu
  const affiche = useRef(initState());  // ce que l'écran voit (instantané + différences en cours)
  const anciens = useRef([]);           // états affichés précédents (les écrans en retard d'un rendu)
  const enCours = useRef([]);           // [{ id, diff, acquisLe }]
  const seq = useRef(0);
  const recalculer = useCallback(() => {
    let e = serveur.current;
    for (const p of enCours.current) e = appliquerDiff(e, p.diff);
    if (e !== affiche.current) anciens.current = [affiche.current, ...anciens.current].slice(0, 4);
    affiche.current = e;
    setAppState(e);
  }, []);
  useEffect(() => {
    let finEcoute = null;
    const arreter = () => { if (finEcoute) { finEcoute(); finEcoute = null; } };
    const finSession = suivreSessionFirebase((connecte) => {
      arreter();
      if (!connecte) {
        serveur.current = initState(); enCours.current = []; recalculer(); setLoading(false);
        return;
      }
      let recu = false;
      finEcoute = onSnapshot(SESSION_DOC,
        (snap) => {
          recu = true;
          const data = snap.exists() ? snap.data() : initState();
          serveur.current = data;
          const t = Date.now();
          // Une différence acquise et déjà contenue dans l'instantané (ou trop
          // ancienne) n'a plus à être rejouée.
          enCours.current = enCours.current.filter((p) => !p.acquisLe
            || (t - p.acquisLe < GARDE_ACQUIS_MS && !egal(appliquerDiff(data, p.diff), data)));
          recalculer();
          setLoading(false);
        },
        (err)  => {
          console.error("Firestore error:", err);
          if (!recu) {
            // Refus de LECTURE (pas de session Aramis, session d'un autre
            // portail) : pas d'écran d'erreur, une séance vide.
            if (codeDe(err) === "permission-denied") { setLoading(false); return; }
            setError(err.message); setLoading(false); return;
          }
          ecouteCoupee.current = true;
          journaliserSeance("Écoute de la séance interrompue", err);
          toast.warn(avisSeance(MSG_ECOUTE_COUPEE, err), { duration: 0 });
        }
      );
    });
    return () => { finSession(); arreter(); };
  }, [recalculer]);
  // Rend VRAI une fois l'écriture acquise, FAUX si elle a échoué ou n'a pas
  // été tentée, et ne lève jamais : la plupart des appelants n'attendent pas
  // la promesse.
  const saveState = useCallback(async (newState, opts) => {
    const { messageEchec, durable, arrierePlan, avisGroupe } = opts || {};
    const durables = avisDurables.current;
    let t = Date.now();
    let err = MSG_ECRITURE_ECOUTE_COUPEE;
    if (!ecouteCoupee.current) {
      if (_isInviteSessionLS()) err = MSG_INVITE;
      else if (arrierePlan && t - echecFond.current < RAFALE_ECHEC_MS) return false;
      else {
        const diff = calculerDiff(affiche.current, newState, anciens.current);
        const reussir = () => {
          dernierAvis.current = echecFond.current = 0;
          if (durables.has(messageEchec)) { toast.dismiss(durables.get(messageEchec)); durables.delete(messageEchec); }
          return true;
        };
        if (!diff) return reussir();
        const geste = { id: ++seq.current, diff, acquisLe: 0 };
        enCours.current = [...enCours.current, geste];
        recalculer();
        try {
          mesurerEcriture("saveState");
          await animer("ecrire", { diff });
          geste.acquisLe = Date.now();
          return reussir();
        } catch (e) {
          console.error("Save error:", e);
          enCours.current = enCours.current.filter((p) => p !== geste);
          recalculer();
          journaliserSeance("Séance non enregistrée", e);
          t = Date.now();                 // l'envoi a pu durer
          if (arrierePlan) echecFond.current = t;
          err = e;
        }
      }
    }
    if ((arrierePlan || avisGroupe) && t - dernierAvis.current < RAFALE_ECHEC_MS) return false;
    dernierAvis.current = t;
    const message = messageEchec || MSG_ECHEC_ECRITURE;
    const corps = typeof err === "string" ? err : avisSeance(message, err);
    if (!durable) { toast.error(corps); return false; }
    if (durables.has(message)) toast.dismiss(durables.get(message));
    durables.set(message, toast.error(corps, { duration: 0 }));
    return false;
  }, [recalculer]);
  return { appState, saveState, loading, error };
}

// ── AGORA — Configuration ─────────────────────────────────────────────────────
// Clés de l'ancienne console super-admin (audit du 28/09/2026) : l'empreinte
// du mot de passe (SHA-256 NON salé) et la graine TOTP, rangées dans
// config/settings — document LU PAR TOUT VISITEUR dès le chargement de la page
// (règle `read: if true`) et vérifiées dans le navigateur. La console est
// supprimée ; les deux valeurs seront purgées de la base APRÈS la mise en
// ligne de cet écran. D'ici là, et après :
//   • elles ne restent pas dans l'état React (plus rien ne les lit) ;
//   • saveConfig ne les ÉCRIT JAMAIS. Il écrit en `merge` : une clé absente de
//     l'objet n'est ni modifiée ni supprimée, donc une règle qui gèle ces clés
//     (`affectedKeys`) laisse passer tous les enregistrements ordinaires, même
//     tant que les secrets existent encore en base. Ne jamais passer en
//     setDoc sans merge : ce serait les SUPPRIMER, donc les « affecter ».
// Les noms exacts ne figurent pas dans src/ (garde src/superAdminLegacy.test.js) :
// on reconnaît la famille par son préfixe, qui ne sert à rien d'autre.
const CLE_CONSOLE_SUPER_ADMIN = /^superAdmin/;
export function sansClesConsoleSuperAdmin(cfg) {
  if (!cfg || typeof cfg !== "object") return cfg;
  const out = {};
  for (const [k, v] of Object.entries(cfg)) if (!CLE_CONSOLE_SUPER_ADMIN.test(k)) out[k] = v;
  return out;
}

// Clés écrites par le SERVEUR seul (01/10/2026) : `sharepoint` désigne le site
// et les dossiers que graphProxy ouvre avec un jeton d'application qui lit et
// écrit tout le tenant ; firestore.rules en refuse toute modification depuis
// le navigateur (seule porte : l'action `enregistrerConfig`, super-admin), et
// `sharepointEchange` ne s'écrit que par la console Firebase. Elles restent
// LUES (l'écran Intégration SharePoint les affiche) mais saveConfig ne les
// renvoie jamais : en `merge`, une clé absente n'est pas touchée, donc un état
// périmé ne fait pas refuser un réglage ordinaire.
const CLES_ECRITES_PAR_LE_SERVEUR = ["sharepoint", "sharepointEchange"];
export function sansClesServeur(cfg) {
  if (!cfg || typeof cfg !== "object") return cfg;
  const out = {};
  for (const [k, v] of Object.entries(cfg)) if (!CLES_ECRITES_PAR_LE_SERVEUR.includes(k)) out[k] = v;
  return out;
}

export function useConfig() {
  const [config,  setConfig]  = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    const unsub = onSnapshot(CONFIG_DOC, (snap) => {
      setConfig(snap.exists() ? sansClesConsoleSuperAdmin(snap.data()) : {}); setLoading(false);
    }, (err) => {
      console.error("useConfig error:", err.message);
      setConfig({}); setLoading(false);
    });
    return () => unsub();
  }, []);
  const saveConfig = useCallback(async (newConfig) => {
    exigerEcritureInvite();
    mesurerEcriture("saveConfig");
    await setDoc(CONFIG_DOC, sansClesServeur(sansClesConsoleSuperAdmin(newConfig)), { merge: true });
  }, []);
  return { config, saveConfig, loading };
}

// ── AGORA — Listes membres (mutuelles) ───────────────────────────────────────
// Champs couleur ajoutés : couleur1 (majeure), couleur2 (mineure), couleur3 (tertiaire)
// Rétrocompatible : les mutuelles existantes sans couleurs fonctionnent normalement.
export function useMemberLists() {
  const [lists,   setLists]   = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    const unsub = onSnapshot(LISTS_COL, (snap) => {
      const data = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      data.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
      setLists(data); setLoading(false);
    });
    return () => unsub();
  }, []);
  const saveList = useCallback(async (list) => {
    exigerEcritureInvite();
    let _before = null;
    try { const _s = await getDoc(doc(db, "memberLists", list.id)); _before = _s.exists() ? _s.data() : null; } catch { /* noop */ }
    mesurerEcriture("saveList");
    await setDoc(doc(db, "memberLists", list.id), list);
    _logEdit({ collection: "memberLists", entityId: list.id, entityLabel: list.name || list.nom || list.id, before: _before, after: list, action: _before ? "edit" : "create" });
  }, []);
  const deleteList = useCallback(async (id) => {
    exigerEcritureInvite();
    await deleteDoc(doc(db, "memberLists", id));
  }, []);
  return { lists, saveList, deleteList, loading };
}

// ── AGORA — Délégués ──────────────────────────────────────────────────────────
const DELEGATES_COL = collection(db, "delegates");

// ── Migration auto silencieuse : normalise les emails non normalisés ─────────
// Déclenchée 1 fois par session, corrige automatiquement les emails importés
// en masse (Excel) qui n'auraient pas été normalisés au moment de l'import.
// Idempotent : ne touche que les documents qui ont besoin d'être corrigés.
let _emailMigrationDone = false;
async function autoMigrateEmails(delegates) {
  if (_emailMigrationDone) return;
  // Ne migrer QUE si l'utilisateur a des droits admin (stockage localStorage)
  try {
    const raw = typeof localStorage !== "undefined" ? localStorage.getItem("aramisSession") : null;
    const session = raw ? JSON.parse(raw) : null;
    const isAdmin = session?.role === "admin" || session?.role === "superadmin";
    if (!isAdmin) return;
  } catch { return; }

  // Détecter les emails qui ont besoin d'être normalisés
  const toFix = delegates.filter(d => {
    if (d.supprime || d.actif === false) return false;
    const raw = d.email || "";
    if (!raw) return false;
    return raw !== normalizeEmail(raw);
  });

  if (toFix.length === 0) {
    _emailMigrationDone = true;
    return;
  }

  console.log(`[AutoMigrate] ${toFix.length} email(s) à normaliser en arrière-plan...`);
  const { writeBatch } = await import("firebase/firestore");
  const batch = writeBatch(db);
  let count = 0;
  for (const d of toFix) {
    const norm = normalizeEmail(d.email);
    if (norm && norm !== d.email) {
      batch.set(doc(db, "delegates", d.id), { email: norm, dateMaj: Date.now() }, { merge: true });
      count++;
      // Firestore limite à 500 writes par batch
      if (count >= 400) break;
    }
  }
  if (count > 0) {
    await batch.commit();
    console.log(`[AutoMigrate] ✅ ${count} email(s) normalisés automatiquement.`);
  }
  _emailMigrationDone = true;
}

// Garde « session admin » (module) : même double-lecture que la restauration
// de session d'App.jsx (localStorage puis repli sessionStorage).
function _isAdminSessionLS() {
  try {
    const raw = (() => {
      try { return localStorage.getItem("aramis_session"); }
      catch { try { return sessionStorage.getItem("aramis_session"); } catch { return null; } }
    })();
    const session = raw ? JSON.parse(raw) : null;
    return session?.role === "admin" || session?.role === "superadmin";
  } catch { return false; }
}

// Identifiant de la fiche de la session (même double lecture) : sert à
// reconnaître l'intéressé sur SA propre fiche (S3, assistantes).
function _sessionIdLS() {
  try {
    const raw = (() => {
      try { return localStorage.getItem("aramis_session"); }
      catch { try { return sessionStorage.getItem("aramis_session"); } catch { return null; } }
    })();
    const session = raw ? JSON.parse(raw) : null;
    return (session && session.id) || null;
  } catch { return null; }
}

// Identifiant et rôle de la session (même double lecture) : reconnaître la
// session du super-administrateur sur SA fiche (retrait du rôle d'admin).
function _sessionLS() {
  try {
    const raw = (() => {
      try { return localStorage.getItem("aramis_session"); }
      catch { try { return sessionStorage.getItem("aramis_session"); } catch { return null; } }
    })();
    const session = raw ? JSON.parse(raw) : null;
    return session ? { id: session.id || null, role: session.role || null } : null;
  } catch { return null; }
}

// Session INVITÉE (role "invite" — dirigeants de mutuelles candidates) :
// consultation seule GLOBALE. Garde-fou du front pour les écritures Firestore
// directes passant par ce hook — les Cloud Functions refusent déjà tout invité
// (validateCaller, options.allowInvite). NB : garde UI, pas une étanchéité
// cryptographique (les rules Firestore restent ouvertes — chantier séparé).
export function _isInviteSessionLS() {
  try {
    const raw = (() => {
      try { return localStorage.getItem("aramis_session"); }
      catch { try { return sessionStorage.getItem("aramis_session"); } catch { return null; } }
    })();
    const session = raw ? JSON.parse(raw) : null;
    return session?.role === "invite";
  } catch { return false; }
}
// À appeler en tête de chaque setter : lève pour une session invitée (le
// composant appelant affiche l'erreur via son try/catch → toast).
function exigerEcritureInvite() {
  if (_isInviteSessionLS()) throw new Error("Compte invité : consultation seule — aucune modification possible.");
}

// ── Migration one-shot (perf P3) : photos base64 hors des fiches delegates ──
// Les photos (data-URL, ~40-115 Ko chacune) pesaient 57 % de la collection
// delegates téléchargée pré-login par tous les clients. Elles déménagent dans
// delegatePhotos/{delegateId}. Sweep déclenché au boot d'une session ADMIN
// (même pattern qu'autoMigrateEmails/syncMemberLists) sur la liste NON
// filtrée (les fiches soft-deleted pèsent aussi sur le wire). Idempotent et
// auto-cicatrisant : un vieux client qui ré-écrirait un data-URL dans une
// fiche sera re-strippé au boot admin suivant.
let _photoMigrationDone = false;
async function migratePhotosToCollection(allDelegates) {
  if (_photoMigrationDone) return;
  if (!_isAdminSessionLS()) return;
  const toMove = allDelegates.filter(d =>
    typeof d.photoUrl === "string" && d.photoUrl.startsWith("data:"));
  if (toMove.length === 0) { _photoMigrationDone = true; return; }
  console.log(`[MigrationPhotos] ${toMove.length} photo(s) à déplacer vers delegatePhotos...`);
  const { writeBatch } = await import("firebase/firestore");
  // 2 writes par fiche (photo + fiche allégée), limite batch 500 → paquets de 200
  for (let i = 0; i < toMove.length; i += 200) {
    const batch = writeBatch(db);
    for (const d of toMove.slice(i, i + 200)) {
      batch.set(doc(db, "delegatePhotos", d.id), { photoUrl: d.photoUrl, dateMaj: Date.now() });
      batch.update(doc(db, "delegates", d.id), { photoUrl: "", dateMaj: Date.now() });
    }
    await batch.commit();
  }
  console.log(`[MigrationPhotos] ✅ ${toMove.length} photo(s) déplacées.`);
  _photoMigrationDone = true;
}

// Cache module des photos (delegatePhotos) : alimenté par l'abonnement de
// useDelegates, consulté par saveDelegate pour router les écritures.
let _photosCache = {};

// `photosEnabled` : l'abonnement delegatePhotos n'a d'intérêt qu'une fois
// connecté (l'écran de login n'affiche aucun avatar) — même logique de
// gating que les autres hooks. Le tableau retourné FUSIONNE les photos dans
// les fiches (d.photoUrl) : aucun consommateur à modifier.
export function useDelegates(photosEnabled = false) {
  const [rawDelegates, setRawDelegates] = useState([]);
  // Comptes techniques : jamais mêlés à `delegates`, exposés à part pour le
  // seul écran super-admin qui doit pouvoir les inventorier.
  const [techniques, setTechniques] = useState([]);
  const [photos, setPhotos] = useState({});
  const [loading, setLoading] = useState(true);

  // Synchronise automatiquement les listes de vote (memberLists) quand les délégués changent
  const syncMemberLists = useCallback(async (delegates) => {
    try {
      // Réservé aux sessions admin (perf boot) : ce sync lisait memberLists en
      // entier et émettait d'éventuels batch writes à CHAQUE snapshot delegates,
      // y compris chez les visiteurs déconnectés sur l'écran de login — travail
      // réseau inutile pendant la phase critique du boot, et courses d'écriture
      // quand plusieurs clients démarrent en même temps (réunions).
      if (!_isAdminSessionLS()) return;
      // Récupérer toutes les listes existantes
      const { getDocs, writeBatch } = await import("firebase/firestore");
      const listsSnap = await getDocs(LISTS_COL);
      const lists = listsSnap.docs.map(d => ({ id: d.id, ...d.data() }));

      // Pour chaque liste non-auto (mutuelles réelles)
      const autoIds = ["__auto_ag__","__auto_ca__","__auto_bureau__","__auto_cd__","__auto_dg__","__auto_pr__"];
      const mutuelleLists = lists.filter(l => !autoIds.includes(l.id));

      if (mutuelleLists.length === 0) return;

      const batch = writeBatch(db);
      let changed = 0;

      for (const list of mutuelleLists) {
        // Trouver les délégués actifs de cette mutuelle
        // CORRECTION 2026-05 : matching STRICT (===) au lieu de .includes() loose.
        // L'ancien matching loose générait des faux positifs : un délégué portant
        // "SMH Hauts-de-France" était attribué à la liste "SMH" car la sous-chaîne
        // matche, ce qui masquait l'incohérence de paramétrage. Désormais on n'attribue
        // que sur match exact normalisé (NFD + lowercase + trim) — les valeurs
        // non-standard doivent être réconciliées via le panel Diagnostic > Mutuelles.
        const normalize = (s) => (s || "").normalize("NFD")
          .replace(/[̀-ͯ]/g, "").toLowerCase().trim();
        const mutNameNorm = normalize(list.name);
        const membres = delegates.filter(d => {
          if (d.actif === false) return false;
          return normalize(d.mutuelle) === mutNameNorm;
        });

        // Construire la liste members synchronisée
        const newMembers = membres.map(d => ({
          id: d.id,
          prenom: d.prenom || "",
          nom: d.nom || "",
          email: d.email || "",
          fonction: d.fonction || "",
          roles: d.roles || [],
        }));

        // Vérifier si mise à jour nécessaire (comparer ids)
        const existingIds = (list.members || []).map(m => m.id).sort().join(",");
        const newIds = newMembers.map(m => m.id).sort().join(",");
        if (existingIds !== newIds) {
          batch.update(doc(db, "memberLists", list.id), {
            members: newMembers,
            dateMaj: Date.now(),
          });
          changed++;
        }
      }

      if (changed > 0) {
        await batch.commit();
        console.log(`syncMemberLists: ${changed} liste(s) mise(s) à jour`);
      }
    } catch (e) {
      console.error("syncMemberLists error:", e.message);
    }
  }, []);

  useEffect(() => {
    let hasRunEmailMigration = false;
    const unsub = onSnapshot(DELEGATES_COL, (snap) => {
      const all = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      all.sort((a, b) => (a.nom || "").localeCompare(b.nom || "", "fr") || (a.prenom || "").localeCompare(b.prenom || "", "fr"));
      // Filtre par défaut : exclut les délégués supprimés (soft-delete via
      // flag `supprime: true`). Les écrans qui ont besoin de voir les
      // supprimés (audit / restauration) doivent passer par une autre
      // source. Cela résout les doublons résiduels où un délégué supprimé
      // continuait d'apparaître dans des listes (gouvernance, mutuelles,
      // diffusions) parce que les filtres oubliaient de checker `supprime`.
      // Cohérent avec App.jsx (ligne 5489) : exclut les délégués marqués
      // soit supprimés, soit désactivés. Ces deux flags sont historiquement
      // équivalents (introduits à différents moments) — un délégué qui a
      // l'un ou l'autre est invisible côté UI utilisateur.
      // `technique: true` — comptes NON HUMAINS (vérification, automates).
      // Ils n'ont ni e-mail, ni mutuelle, ni rôle, et ne doivent apparaître dans
      // AUCUNE surface utilisateur : ni annuaire, ni ⌘K, ni sélecteur de
      // destinataires, ni compteur, ni organigramme, ni export.
      //
      // Ce point est LE choke point du front : les ~150 composants qui affichent
      // des délégués les reçoivent en prop depuis ici. Filtrer à cet endroit les
      // couvre tous d'un coup — voir `delegatesTechniques` plus bas pour la
      // visibilité super-admin.
      // `role === "invite"` — comptes INVITÉS (dirigeants de mutuelles
      // candidates, lecture seule) : invisibles pour tous, comme les comptes
      // techniques — ni annuaire, ni ⌘K, ni sélecteurs, ni memberLists (le
      // syncMemberLists plus bas reçoit la liste DÉJÀ filtrée).
      const data = all.filter(d => !d.supprime && d.actif !== false && !d.technique && d.role !== "invite");
      setRawDelegates(data);
      // Comptes techniques exposés SÉPARÉMENT, sans lecture supplémentaire :
      // le super-admin doit pouvoir constater leur existence, personne d'autre.
      setTechniques(all.filter(d => d.technique === true));
      setLoading(false);
      // Synchroniser les listes après chaque changement (sur la liste
      // FILTRÉE — les supprimés ne doivent plus peupler les memberLists)
      syncMemberLists(data);
      // Migration auto des emails non normalisés (1 fois par session, admin only)
      if (!hasRunEmailMigration) {
        hasRunEmailMigration = true;
        autoMigrateEmails(data).catch(e => console.warn("[AutoMigrate] Échec:", e.message));
      }
      // Migration photos → delegatePhotos (1 fois par session, admin only) —
      // sur la liste NON filtrée : les fiches soft-deleted pèsent aussi.
      migratePhotosToCollection(all).catch(e => console.warn("[MigrationPhotos] Échec:", e.message));
    });
    return () => unsub();
  }, [syncMemberLists]);

  // Abonnement aux photos (post-login) + cache module pour saveDelegate.
  useEffect(() => {
    if (!photosEnabled) {
      _photosCache = {};
      setPhotos(prev => (Object.keys(prev).length ? {} : prev));
      return undefined;
    }
    const unsub = onSnapshot(collection(db, "delegatePhotos"), (snap) => {
      const map = {};
      snap.docs.forEach(d => { map[d.id] = d.data()?.photoUrl || ""; });
      _photosCache = map;
      setPhotos(map);
    }, (err) => console.warn("useDelegates photos error (non bloquant):", err.message));
    return () => unsub();
  }, [photosEnabled]);

  // Fusion transparente : les consommateurs continuent de lire d.photoUrl.
  // delegatePhotos fait AUTORITÉ quand un doc photo existe (y compris photo
  // retirée = ""), sinon on garde l'éventuel photoUrl legacy de la fiche
  // (période de transition avant le passage du sweep admin).
  const delegates = useMemo(() => {
    if (!Object.keys(photos).length) return rawDelegates;
    return rawDelegates.map(d =>
      photos[d.id] !== undefined ? { ...d, photoUrl: photos[d.id] } : d);
  }, [rawDelegates, photos]);

  const saveDelegate = useCallback(async (delegate, options) => {
    exigerEcritureInvite();
    mesurerEcriture("saveDelegate");
    // VERROU (16/09/2026) : aucun identifiant ne part sur la fiche, quelle que
    // soit l'origine de l'objet. `delegates/` est lisible ET modifiable par
    // n'importe qui : un mot de passe, un sel, un secret TOTP ou une clé de
    // passkey qui y atterrit est publié — et le serveur ne les lit plus. Les
    // gestes légitimes passent par des Cloud Functions (aramisDefinirMotDePasse,
    // aramisSetupTOTP, passkeyEnroler, passkeyRetirer). Le drapeau
    // `totp:{verified:true}` (sans secret) reste : c'est un état d'interface.
    const { passwordHash: _ph, passwordSalt: _ps, passkey: _pk, passkeys: _pks, ...sansSecrets } = delegate || {};
    if (sansSecrets.totp && typeof sansSecrets.totp === "object" && sansSecrets.totp.secret) {
      sansSecrets.totp = { verified: !!sansSecrets.totp.verified };
    }
    delegate = sansSecrets;
    // Garantie : toujours normaliser l'email à l'écriture
    // → Corrige automatiquement les emails importés non normalisés.
    let normalized = {
      ...delegate,
      email: delegate.email ? normalizeEmail(delegate.email) : "",
      emailAliases: (delegate.emailAliases || []).map(normalizeEmail).filter(Boolean),
    };
    // Perf P3 : le raster base64 ne vit plus dans la fiche. Un data-URL
    // entrant (ajout/changement de photo via MonProfilView, ou fiche fusionnée
    // par le tableau merged) part dans delegatePhotos ; photoUrl:"" avec une
    // photo connue = retrait volontaire → on vide aussi delegatePhotos.
    try {
      if (typeof normalized.photoUrl === "string" && normalized.photoUrl.startsWith("data:")) {
        if (_photosCache[delegate.id] !== normalized.photoUrl) {
          await setDoc(doc(db, "delegatePhotos", delegate.id), { photoUrl: normalized.photoUrl, dateMaj: Date.now() });
        }
        normalized.photoUrl = "";
      } else if (normalized.photoUrl === "" && _photosCache[delegate.id]) {
        await setDoc(doc(db, "delegatePhotos", delegate.id), { photoUrl: "", dateMaj: Date.now() });
      }
    } catch (e) { console.warn("saveDelegate photo:", e.message); }
    let _before = null;
    let _beforeLu = false;
    try { const _s = await getDoc(doc(db, "delegates", delegate.id)); _before = _s.exists() ? _s.data() : null; _beforeLu = true; } catch { /* noop */ }
    // « Prénom NOM » (30/09/2026, src/nomsPersonnes.js) : un prénom ou un nom
    // SAISI (différent de la fiche en base, ou fiche neuve) est mis en forme
    // s'il est sûr ; un cas à vérifier garde la saisie. Une recopie à
    // l'identique n'est jamais touchée, ni une fiche qu'on n'a pas pu relire.
    if (_beforeLu) normalized = { ...normalized, ...miseEnFormeSaisie(normalized, _before) };
    // Rôles (15/10/2026) : un éditeur qui dit ce qu'il a VU à l'ouverture
    // (`options.rolesVus`) n'écrit que ses ajouts et ses retraits ; un rôle
    // changé entre-temps (habilitation AI ou CI posée par le DG, autre
    // administrateur) n'est plus effacé par une fiche ouverte avant
    // (src/rolesHabilitations.js, fusionnerRoles).
    if (_beforeLu && _before && options && Array.isArray(options.rolesVus) && Array.isArray(normalized.roles)) {
      normalized = { ...normalized, roles: fusionnerRoles(_before.roles, options.rolesVus, normalized.roles) };
    }
    // FB10a (30/09/2026) : la règle ne laisse CHANGER les champs d'autorité
    // (rôles, mandats, mutuelle, assistantes, statut) et l'identité (e-mail,
    // alias : une clé d'autorité pour la connexion et les notes de frais)
    // qu'à un administrateur authentifié. Hors session administratrice, on
    // reprend donc les valeurs EN BASE : une copie périmée de la fiche
    // (bienvenue, favoris) ou un repli sur la session n'efface plus un rôle ni
    // un mandat, et l'écriture passe. Un changement d'adresse VOULU est refusé
    // en clair plutôt qu'ignoré. Une session administratrice écrit telle
    // quelle : si la règle refuse (session Firebase absente), l'erreur remonte
    // au lieu d'un succès trompeur (src/champsAutorite.js).
    let assistantesSurSaFiche = false;
    if (_before && !_isAdminSessionLS()) {
      // Une différence de simple normalisation (casse, blancs) n'est pas un
      // changement voulu : la valeur en base est reprise sans bruit.
      const norm = (v) => (typeof v === "string" ? normalizeEmail(v) : v);
      const avantNorm = { email: norm(_before.email),
        emailAliases: (Array.isArray(_before.emailAliases) ? _before.emailAliases : []).map(norm).filter(Boolean) };
      // Un champ ABSENT de l'objet passé (copie partielle, repli sur la
      // session) n'est pas un changement voulu : seul un champ porté compte.
      const voulu = {
        email: delegate.email !== undefined ? normalized.email : avantNorm.email,
        emailAliases: delegate.emailAliases !== undefined ? normalized.emailAliases : avantNorm.emailAliases,
      };
      if (!identiteInchangee(avantNorm, voulu)) {
        const refus = new Error("Une adresse e-mail sert à se connecter et à ouvrir des droits : seul un administrateur Enio la modifie. Demandez-le à l'équipe.");
        refus.code = "permission-denied";
        throw refus;
      }
      // S3 (décision de Pierre du 30/09/2026) : l'intéressé change les
      // assistantes de SA fiche. Seulement quand l'écran le demande
      // (`{ assistantes: true }`, fiche membre du pôle) : une copie périmée
      // de sa propre fiche (bienvenue, Ma semaine) n'efface pas son choix.
      assistantesSurSaFiche = !!(options && typeof options === "object" && options.assistantes === true)
        && !!delegate.id && _sessionIdLS() === delegate.id;
      if (assistantesSurSaFiche && Array.isArray(normalized.assistantes)
        && normalized.assistantes.length > MAX_ASSISTANTES) {
        const refus = new Error(`Au plus ${MAX_ASSISTANTES} assistant·e·s par fiche.`);
        refus.code = "invalid-argument";
        throw refus;
      }
      normalized = preserverAutorite(normalized, _before,
        { autorite: false, identite: false, assistantes: assistantesSurSaFiche });
    }
    // Fiche supprimée ou désactivée = plus administratrice (demande de Pierre
    // du 01/10/2026) : le rôle part dans la MÊME écriture, et le geste a sa
    // propre entrée de journal (src/retraitRoleAdmin.js). Seule une session
    // d'administration change un statut ou un rôle (FB10a) : hors d'elle, la
    // fiche garde ses valeurs en base (preserverAutorite, ci-dessus), et
    // retirer le rôle ferait refuser l'écriture.
    let retraitAdmin = null;
    if (_isAdminSessionLS()) {
      const plan = planRetraitRoleAdmin({ avant: _before, apres: normalized, id: delegate.id, session: _sessionLS() });
      if (plan.refus) throw plan.refus;
      if (plan.retrait) {
        normalized = { ...normalized, ...plan.champs };
        retraitAdmin = plan.retrait;
      }
    }
    // Le rôle d'administrateur se donne et se retire par le super-administrateur
    // SEUL (12/10/2026, firestore.rules qualiteAdminInchangee) : refus clair
    // avant toute écriture plutôt qu'un refus des règles. Une fiche qu'on n'a
    // pas pu relire est laissée aux règles. Une réécriture qui GARDE le rôle
    // et le statut (Ma semaine, favoris) n'est jamais concernée.
    if (_beforeLu && !sessionSuperAdmin(_sessionLS()) && changeQualiteAdmin(_before, normalized)) {
      throw refusQualiteAdmin();
    }
    // Même réserve pour l'identité d'un administrateur en exercice (relecture
    // adverse du 12/10/2026) : son e-mail et ses alias ouvrent sa session.
    if (_beforeLu && !sessionSuperAdmin(_sessionLS()) && identiteAdminChange(_before, normalized)) {
      throw refusIdentiteAdmin();
    }
    // Le document EXISTANT peut encore porter des champs d'identifiant (fiches
    // d'avant la purge) : setDoc sans merge les efface, c'est voulu.
    try {
      await setDoc(doc(db, "delegates", delegate.id), normalized);
    } catch (e) {
      // FB10a : un refus des règles dit pourquoi (session d'administrateur
      // sans connexion Firebase vérifiée, le plus souvent) au lieu du
      // « Missing or insufficient permissions » du SDK.
      if (e && e.code === "permission-denied") {
        const refus = new Error(assistantesSurSaFiche && !assistantesInchangees(_before, normalized)
          ? "Enregistrement refusé : votre assistant·e ne change qu'avec une connexion vérifiée. Reconnectez-vous à Aramis et réessayez."
          // Vrai avant comme après le geste S2 (règles FB10a) : d'autres
          // verrous refusent aussi (identifiants, adresse du super-admin).
          : "Enregistrement refusé par les règles de sécurité. Changer les rôles, mandats, mutuelle ou adresse e-mail d'une fiche demande une connexion d'administrateur vérifiée. Reconnectez-vous à Aramis et réessayez ; si le refus persiste, prévenez l'équipe Enio.");
        refus.code = e.code;
        throw refus;
      }
      throw e;
    }
    _logEdit({ collection: "delegates", entityId: delegate.id, entityLabel: `${normalized.prenom || ""} ${normalized.nom || ""}`.trim(), before: _before, after: normalized, action: _before ? "edit" : "create" });
    if (retraitAdmin) await _logRetraitRoleAdmin({ id: delegate.id, fiche: normalized, retrait: retraitAdmin });
  }, []);
  const deleteDelegate = useCallback(async (id) => {
    exigerEcritureInvite();
    // Soft-delete : marquer comme supprimé (les Firestore rules interdisent deleteDoc).
    // Nettoie aussi les références "actives" qui faisaient survivre le délégué dans
    // les écrans de gouvernance (CA/AG/CODIR/Bureau) et provoquait des doublons /
    // faux positifs : statuts de mandats et rôles d'instance.
    // On lit le doc courant pour préserver les autres champs des objets mandat*.
    // La fiche DOIT être relue : c'est elle qui dit si un rôle d'administration
    // est à retirer (ou si c'est celle du super-administrateur, qui ne se
    // supprime pas). Sans elle, on ne supprime pas à l'aveugle.
    const ref = doc(db, "delegates", id);
    let cur;
    try {
      const snap = await getDoc(ref);
      cur = snap.exists() ? snap.data() : {};
    } catch {
      const refus = new Error("Impossible de relire la fiche avant de la supprimer. Vérifiez la connexion et réessayez.");
      refus.code = "unavailable";
      throw refus;
    }
    const closeMandat = (m) => (m && typeof m === "object")
      ? { ...m, statut: "termine" }
      : { statut: "termine" };
    const statut = { actif: false, supprime: true };
    // Fiche supprimée = plus administratrice : rôle retiré dans la MÊME
    // écriture, geste tracé « retrait_role_admin » (src/retraitRoleAdmin.js).
    const plan = planRetraitRoleAdmin({ avant: cur, apres: { ...cur, ...statut }, id, session: _sessionLS() });
    if (plan.refus) throw plan.refus;
    // Supprimer la fiche d'un administrateur lui retire la qualité :
    // super-administrateur seul (12/10/2026), refus avant toute écriture.
    if (!sessionSuperAdmin(_sessionLS()) && changeQualiteAdmin(cur, { ...cur, ...statut, ...(plan.retrait ? plan.champs : {}) })) {
      throw refusQualiteAdmin();
    }
    await setDoc(ref, {
      ...statut,
      dateSuppression: new Date().toISOString(),
      mandatCA:     closeMandat(cur.mandatCA),
      mandatAG:     closeMandat(cur.mandatAG),
      mandatBureau: closeMandat(cur.mandatBureau),
      mandatCODIR:  closeMandat(cur.mandatCODIR),
      // Vide les rôles instance (les listes Conseil/AG/CODIR se calculent aussi par roles[])
      roles: [],
      ...(plan.retrait ? plan.champs : {}),
    }, { merge: true });
    if (plan.retrait) await _logRetraitRoleAdmin({ id, fiche: cur, retrait: plan.retrait });
  }, []);
  // `delegatesTechniques` n'est consommé QUE par le panneau super-admin
  // (SuperAdminPanel). Ne jamais le passer aux composants métier : il existe
  // pour l'inventaire et la révocation, pas pour l'usage.
  return { delegates, delegatesTechniques: techniques, saveDelegate, deleteDelegate, loading };
}

// ── AGORA — Gouvernance ───────────────────────────────────────────────────────
const GOUV_DOC = doc(db, "gouvernance", "enio");

export function useGouvernance(enabled = true) {
  const [gouvernance, setGouvernance] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    if (!enabled) { setGouvernance(prev => (prev === null ? prev : null)); return undefined; }
    const unsub = onSnapshot(GOUV_DOC, (snap) => {
      setGouvernance(snap.exists() ? snap.data() : {
        mandatureAG:     { debut: "2024-07", fin: "2028-07" },
        mandatureCA:     { debut: "2024-07", fin: "2028-07" },
        mandatureBureau: { debut: "2024-07", fin: "2026-07" },
        postesActifs: [],
      });
      setLoading(false);
    });
    return () => unsub();
  }, [enabled]);
  const saveGouvernance = useCallback(async (data) => {
    exigerEcritureInvite();
    mesurerEcriture("saveGouvernance");
    // Les directeurs de domaine et coordinateurs de pôle (orgEnio.domaines)
    // ne s'écrivent plus d'un navigateur : leurs désignations ouvrent des
    // droits dans Hestia (HO2), elles passent par la fonction hestiaAdmin
    // (src/hestia/organigramme.js). Les retirer ici évite aussi qu'une copie
    // périmée, renvoyée avec le reste du document, ne revienne en arrière
    // par la fusion profonde de setDoc (la règle la refuserait en entier).
    // Le reste d'orgEnio (postes de Gouvernance) s'écrit par un administrateur
    // authentifié (firestore.rules, orgEnioPostesInchanges) ; renvoyé
    // inchangé par un autre écran, il passe.
    const sansDomaines = { ...(data || {}) };
    if (sansDomaines.orgEnio && typeof sansDomaines.orgEnio === "object") {
      const { domaines, ...postes } = sansDomaines.orgEnio;
      if (Object.keys(postes).length) sansDomaines.orgEnio = postes;
      else delete sansDomaines.orgEnio;
    }
    await setDoc(GOUV_DOC, sansDomaines, { merge: true });
  }, []);
  return { gouvernance, saveGouvernance, loading };
}

// ── AGORA — Administrateurs ───────────────────────────────────────────────────
// `enabled` (perf boot) : les hooks de collections non nécessaires avant la
// connexion sont désabonnés tant que la session Aramis n'existe pas — le
// canal long-polling reste disponible pour le splash (session/config) et le
// login (delegates/memberLists). Au logout, l'état revient à sa valeur
// initiale pour ne rien laisser fuiter vers l'écran public.
export function useAdmins(enabled = true) {
  const [admins,  setAdmins]  = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    if (!enabled) { setAdmins(prev => (prev.length ? [] : prev)); return undefined; }
    const unsub = onSnapshot(ADMINS_COL, (snap) => {
      const data = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setAdmins(data); setLoading(false);
    });
    return () => unsub();
  }, [enabled]);
  const saveAdmin = useCallback(async (admin) => {
    exigerEcritureInvite();
    mesurerEcriture("saveAdmin");
    await setDoc(doc(db, "admins", admin.id), admin);
  }, []);
  const deleteAdmin = useCallback(async (id) => {
    exigerEcritureInvite();
    // Soft-delete : marquer comme supprimé (les Firestore rules interdisent deleteDoc)
    await setDoc(doc(db, "admins", id), { actif: false, supprime: true, dateSuppression: new Date().toISOString() }, { merge: true });
  }, []);
  return { admins, saveAdmin, deleteAdmin, loading };
}

// ── AGORA — Journal d'audit (30 jours glissants) ─────────────────────────────
const THIRTY_DAYS = 30 * 24 * 3600 * 1000;

// ── Instrumentation des écritures (Étape 10d) — journal « qui a fait quoi » ──
// Acteur courant (positionné par l'app selon la session). Les sauvegardes
// centrales appellent _logEdit() pour tracer before/after dans auditLog →
// alimente le journal d'activité + permet le revert. Best-effort, jamais bloquant.
let _auditActor = "Admin";
export function setAuditActor(name) { _auditActor = (name && String(name).trim()) || "Admin"; }
// Les copies before/after embarquaient les photos base64 des fiches délégués
// (jusqu'à ~100 Ko par photo, ×2 par édition) : le journal devenait la
// collection la plus lourde du boot. On remplace tout data-URL volumineux par
// un marqueur — le revert d'une photo n'a jamais été un cas d'usage du journal.
function _stripDataUrls(obj) {
  if (!obj || typeof obj !== "object") return obj ?? null;
  let out = obj;
  for (const k of Object.keys(obj)) {
    const v = obj[k];
    if (typeof v === "string" && v.startsWith("data:") && v.length > 2048) {
      if (out === obj) out = { ...obj };
      out[k] = "[média retiré du journal]";
    }
  }
  return out;
}
/** Retire d'un instantané de fiche tout ce qui est un identifiant : le
 *  journal `auditLog` est LISIBLE PAR TOUS, et il a conservé pendant des mois
 *  des empreintes de mots de passe et des secrets TOTP (constat du 16/09/2026). */
function _sansIdentifiants(o) {
  if (!o || typeof o !== "object") return o;
  const { passwordHash: _ph, passwordSalt: _ps, passkey: _pk, passkeys: _pks, codeHash: _ch, totpSecret: _ts, ...reste } = o;
  if (reste.totp && typeof reste.totp === "object" && reste.totp.secret) {
    reste.totp = { verified: !!reste.totp.verified };
  }
  return reste;
}

async function _logEdit({ collection: col, entityId, entityLabel, before, after, action }) {
  try {
    const id = newId();
    await setDoc(doc(db, "auditLog", id), {
      id, ts: Date.now(), action: action || "edit", actionCategory: "data_write",
      collection: col || "", entityId: entityId || "", entityLabel: entityLabel || entityId || "",
      before: _sansIdentifiants(_stripDataUrls(before)), after: _sansIdentifiants(_stripDataUrls(after)), adminName: _auditActor,
    });
  } catch { /* journalisation best-effort */ }
}

/** Entrée « retrait_role_admin » du journal (jamais « edit » : voir
 *  src/retraitRoleAdmin.js). Best-effort comme _logEdit : la fiche est déjà
 *  écrite, et l'inventaire d'autorité (functions/scripts/inventaire-autorite.js)
 *  relit l'état réel. */
async function _logRetraitRoleAdmin({ id, fiche, retrait }) {
  try {
    const entreeId = newId();
    await setDoc(doc(db, "auditLog", entreeId), entreeJournalRetrait({ id, fiche, retrait, acteur: _auditActor, entreeId }));
  } catch { /* journalisation best-effort */ }
}

export function useAuditLog(enabled = true) {
  const [auditLog, setAuditLog] = useState([]);
  useEffect(() => {
    if (!enabled) { setAuditLog(prev => (prev.length ? [] : prev)); return undefined; }
    // Borne SERVEUR sur la fenêtre affichée : la purge Cloud Functions est à
    // 90 j (backups.js) alors que l'UI ne montre que 30 j — sans le where, le
    // client téléchargeait jusqu'à 3× plus de documents (avec copies
    // before/after complètes) que ce qu'il affiche. Le filtre client reste en
    // garde-fou (la borne est figée à l'abonnement).
    const q30 = query(AUDIT_COL, where("ts", ">", Date.now() - THIRTY_DAYS));
    const unsub = onSnapshot(q30, (snap) => {
      const now = Date.now();
      const data = snap.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .filter(e => now - e.ts < THIRTY_DAYS)
        .sort((a, b) => b.ts - a.ts);
      setAuditLog(data);
      // Nettoyage > 30 jours : désormais géré côté serveur (Cloud Functions)
      // Les Firestore rules interdisent la suppression côté client pour l'audit trail
    }, (err) => {
      console.warn("useAuditLog error (non bloquant):", err.message);
    });
    return () => unsub();
  }, [enabled]);
  const logAction = useCallback(async ({ action, collection: col, entityId, entityLabel, before, after, adminName }) => {
    const id = newId();
    await setDoc(doc(db, "auditLog", id), {
      id, ts: Date.now(), action,
      collection: col,
      entityId:    entityId    || "",
      entityLabel: entityLabel || entityId || "",
      before:      before      || null,
      after:       after       || null,
      adminName:   adminName   || "Admin",
    });
  }, []);
  return { auditLog, logAction };
}

// ── AGORA — Boîte à idées ─────────────────────────────────────────────────────
export function useIdeaBox() {
  const [ideas, setIdeas] = useState([]);
  useEffect(() => {
    const unsub = onSnapshot(IDEAS_COL, (snap) => {
      const data = snap.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .sort((a, b) => b.ts - a.ts);
      setIdeas(data);
    }, (err) => {
      console.warn("useIdeaBox error (non bloquant):", err.message);
    });
    return () => unsub();
  }, []);
  const saveIdea = useCallback(async (idea) => {
    exigerEcritureInvite();
    const id = newId();
    mesurerEcriture("saveIdea");
    await setDoc(doc(db, "ideaBox", id), { ...idea, id, ts: Date.now() });
  }, []);
  const deleteIdea = useCallback(async (id) => {
    exigerEcritureInvite();
    await deleteDoc(doc(db, "ideaBox", id));
  }, []);
  return { ideas, saveIdea, deleteIdea };
}

// ════════════════════════════════════════════════════════════════════════════════
// ATHOS — Suivi de projet
// ════════════════════════════════════════════════════════════════════════════════

// ── ATHOS — Projets & partenariats ────────────────────────────────────────────
//
// Structure d'un document project :
// {
//   id: string,
//   ref: "2.1",                           // référence domaine.pôle
//   type: "projet" | "partenariat",
//   domaine: string,                      // ex. "DÉVELOPPEMENT & FIDÉLISATION"
//   pole: string,                         // ex. "Offres, distribution et marketing"
//   intitule: string,
//   porteurs: string[],                   // ex. ["C. Charton", "P. Marini"]
//   statut: string,                       // voir STATUTS_PROJET ci-dessous
//   description: string,
//   deadline: number | null,              // timestamp ms
//   clos: boolean,
//   engagements: {                        // clé = id mutuelle (memberList.id)
//     [mutuellleId]: "engage" | "etudie" | "non_interesse" | "solution_alternative" | null
//   },
//   dateCreation: number,                 // timestamp ms
//   dateMaj: number,                      // timestamp ms
// }
//
// Statuts possibles (STATUTS_PROJET) :
//   exploratoire | intermediaire | conclusion | partenariat_ouvert |
//   solution_ouverte | attente | realise | travail_continu | abandonne

export const STATUTS_PROJET = [
  { value: "exploratoire",      label: "Phase exploratoire",                  color: "#F49800" },
  { value: "intermediaire",     label: "Phase intermédiaire",                 color: "#F49800" },
  { value: "conclusion",        label: "Phase de conclusion",                 color: "#1D948A" },
  { value: "partenariat_ouvert",label: "Partenariat ouvert",                  color: "#94C11F" },
  { value: "solution_ouverte",  label: "Solution ouverte à contractualisation",color: "#4a90d9" },
  { value: "attente",           label: "En attente de lancement",             color: "#9b9b9b" },
  { value: "realise",           label: "Réalisé / Clôturé",                   color: "#003F59" },
  { value: "travail_continu",   label: "Travail continu",                     color: "#951B81" },
  { value: "abandonne",         label: "Dossier abandonné",                   color: "#d0021b" },
];

export const ENGAGEMENTS_MUTUELLE = [
  { value: "engage",              label: "Engagée",                    color: "#94C11F" },
  { value: "etudie",             label: "Étudie l'opportunité",        color: "#F49800" },
  { value: "non_interesse",      label: "Pas intéressée",              color: "#d0021b" },
  { value: "solution_alternative",label: "Solution alternative",       color: "#9b9b9b" },
];

export function useProjects(enabled = true) {
  const [projects, setProjects] = useState([]);
  const [loading,  setLoading]  = useState(true);
  useEffect(() => {
    if (!enabled) { setProjects(prev => (prev.length ? [] : prev)); return undefined; }
    const unsub = onSnapshot(PROJECTS_COL, (snap) => {
      const data = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      data.sort((a, b) => (a.ref || "").localeCompare(b.ref || ""));
      setProjects(data); setLoading(false);
    }, (err) => {
      console.warn("useProjects error:", err.message); setLoading(false);
    });
    return () => unsub();
  }, [enabled]);
  // `options.base` (fiche projet ouverte, 20/10/2026) : l'état de la fiche à
  // l'ouverture de la fenêtre. Un champ que la fenêtre n'a pas changé garde la
  // valeur relue en base (src/propositionsProjet.js, fusionnerAvecBase) : une
  // proposition acceptée pendant que la fiche était ouverte n'est plus effacée.
  const saveProject = useCallback(async (projectSaisi, options = {}) => {
    exigerEcritureInvite();
    let project = projectSaisi;
    const id = project.id || newId();
    const projRef = doc(db, "projects", id);

    // Lire l'état antérieur AVANT l'écriture pour détecter un changement
    // de nom du dossier SharePoint lié (composé de ref + intitulé). Sans ça,
    // au prochain affichage des documents, le path SharePoint recalculé ne
    // matcherait plus le dossier existant et la CF graphProxy:listFolder
    // créerait un nouveau dossier vierge — laissant l'ancien orphelin avec
    // tout l'historique des fichiers.
    let oldProject = null;
    try {
      const { getDoc } = await import("firebase/firestore");
      const snap = await getDoc(projRef);
      if (snap.exists()) oldProject = snap.data();
    } catch (e) {
      // Pas bloquant : si la lecture échoue, on continue le save mais on
      // n'essaiera pas de renommer le dossier SharePoint.
      console.warn("[saveProject] lecture ancien doc échouée:", e?.message);
    }
    if (options && options.base && oldProject) {
      project = fusionnerAvecBase(projectSaisi, options.base, oldProject).fiche;
    }

    const _payload = { ...project, id, dateMaj: Date.now(), dateCreation: project.dateCreation || Date.now() };
    mesurerEcriture("saveProject");
    // Par le serveur (fermeture des règles de `projects`, 20/10/2026) : il
    // relit la fiche EN TRANSACTION, refait la fusion avec l'état d'ouverture
    // et vérifie le droit (administrateur, ou porteur rattaché hors porteurs,
    // contacts et mutuelles porteuses). La fusion locale ne sert plus qu'au
    // journal et au nom du dossier SharePoint.
    await projetEcrire("enregistrer", { projet: { ...projectSaisi, id }, base: (options && options.base) || null });
    _logEdit({ collection: "projects", entityId: id, entityLabel: project.intitule || project.ref || id, before: oldProject, after: _payload, action: oldProject ? "edit" : "create" });

    // Synchroniser le dossier SharePoint du projet (best effort, jamais bloquant).
    // - Nouveau projet → on CRÉE le dossier à la bonne racine immédiatement.
    // - Renommage (ref/intitulé) → on RENOMME le dossier existant en miroir
    //   (même dossier, historique conservé) ; repli sur création si l'ancien
    //   dossier n'existe pas encore.
    // Le nommage doit rester strictement aligné avec le subPath de
    // DocumentLibrary dans App.jsx.
    try {
      const buildFolderName = (p) =>
        ((p.ref || p.id || "") + " — " + (p.intitule || ""))
          .replace(/["*:<>?/\\|]/g, "").trim().slice(0, 60).trim();
      const newName = buildFolderName({ ...project, id });
      const oldName = oldProject ? buildFolderName(oldProject) : null;
      if (newName) {
        const { getDoc } = await import("firebase/firestore");
        const cfgSnap = await getDoc(doc(db, "config", "settings"));
        const cfg = cfgSnap.exists() ? cfgSnap.data() : {};
        const projetsFolder = (cfg?.sharepoint?.folders?.projets || "Projets Enio").trim();
        const raw = typeof localStorage !== "undefined"
          ? localStorage.getItem("aramisSession") : null;
        const session = raw ? JSON.parse(raw) : null;
        if (session?.id) {
          const sp = await import("./sharepoint");
          if (oldName && oldName !== newName) {
            // Renommage : tenter le rename, sinon créer le nouveau dossier.
            try {
              await sp.renameFolderByPath(session, projetsFolder + "/" + oldName, newName);
            } catch (e) {
              console.warn("[saveProject] rename SP échoué → création du dossier:", e?.message);
              await sp.ensureFolder(session, projetsFolder + "/" + newName);
            }
          } else if (!oldProject) {
            // Nouveau projet : créer le dossier dès la création.
            await sp.ensureFolder(session, projetsFolder + "/" + newName);
          }
        }
      }
    } catch (e) {
      // Ne pas faire échouer la sauvegarde projet — un admin pourra nettoyer
      // manuellement si besoin. On log fort pour le repérer.
      console.error("[saveProject] sync dossier SharePoint échoué:", e?.message || e);
    }
  }, []);
  const deleteProject = useCallback(async (id) => {
    exigerEcritureInvite();
    await projetEcrire("supprimer", { projetId: id });
  }, []);
  // Met à jour uniquement l'engagement d'une mutuelle sur un projet
  const setEngagement = useCallback(async (projectId, mutuellleId, valeur, meta={}) => {
    exigerEcritureInvite();
    // Lire l'ancienne valeur avant d'écraser
    const { getDoc } = await import("firebase/firestore");
    const snap = await getDoc(doc(db, "projects", projectId));
    const ancienStatut = snap.exists() ? (snap.data().engagements?.[mutuellleId] || "") : "";
    const projIntitule = snap.exists() ? (snap.data().intitule || projectId) : projectId;

    await setDoc(doc(db, "projects", projectId), {
      engagements: { [mutuellleId]: valeur },
      dateMaj: Date.now(),
    }, { merge: true });

    // Trace du changement (journal des administrateurs, centre de
    // notifications du 30/09/2026) ; RELUE comme donnée par le remerciement
    // hebdomadaire (functions/cotisations.js : type, ts, auteur, mutuelle,
    // projId, projIntitule, newStatut) : ne pas retirer ces champs.
    try {
      // Libellés chargés avec le centre de notifications ; indisponibles (hors
      // ligne), la trace part quand même.
      const LIBELLES_ENGAGEMENT = await import("./notificationsGabarits").then((m) => m.LIBELLES_ENGAGEMENT, () => ({}));
      const { addDoc, collection } = await import("firebase/firestore");
      const auteurNom = meta.auteurNom || "Administrateur";
      await addDoc(collection(db, "notifications"), {
        type: "engagement",
        version: 2,
        titre: `${projIntitule} : ${meta.mutuelleNom || mutuellleId}`,
        message: `Statut passé à « ${LIBELLES_ENGAGEMENT[valeur] || "un autre statut"} » par ${auteurNom}.`,
        lien: "athos-projets", params: { projectId },
        auteur: auteurNom,
        auteurId: meta.auteurId || null,
        mutuelle: meta.mutuelleNom || mutuellleId,
        projId: projectId,
        projIntitule,
        ancienStatut,
        newStatut: valeur,
        ts: Date.now(),
        read: false,
      });
    } catch(e) { console.error("notif engagement:", e.message); }
  }, []);
  return { projects, saveProject, deleteProject, setEngagement, loading };
}

// Note : la collection `missionsStatutaires` (sprint B initial) a été
// abandonnée — les missions permanentes sont des projets de la collection
// `projects` filtrés sur statutMaturite="mission_permanente". Cf. refacto
// 2026-05 dans le plan ~/.claude/plans/on-va-cr-er-un-vast-river.md.

// ── ATHOS — Commentaires / discussions par projet ────────────────────────────
//
// Sous-collection projects/{projId}/commentaires/{commId}.
// Permet aux mutuelles, porteurs et admins de discuter sur chaque projet.
//
// Structure d'un commentaire :
// {
//   id, projId,
//   parentId: null | string,              // null = top-level, sinon ID parent (réponse)
//   auteurId, auteurNom, auteurFonction, auteurMutuelle,
//   auteurType: "admin" | "porteur" | "mutuelle",
//   texte: string,
//   pieceJointe: { name, sharepointPath, sharepointId, size, type } | null,
//   visibilite: "prive" | "public",       // privé = mutuelle + admins + porteurs
//   statut: "publie" | "en_attente" | "rejete",
//   dateCreation, dateMaj,
//   validePar: aramisUserId | null,
//   motifRejet: string | null,
// }
//
// Workflow :
// - admin/porteur Enio    → publication directe (statut=publie, visibilite=public)
// - mutuelle (top-level)  → en_attente, visibilite=prive
// - mutuelle (en réponse) → publication directe dans le fil ; admin peut
//                           basculer la visibilité globale du fil ensuite
// - admin peut à tout moment : valider/refuser, basculer la visibilité,
//                              supprimer
export function useProjectCommentaires(projId) {
  const [commentaires, setCommentaires] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    if (!projId) { setCommentaires([]); setLoading(false); return; }
    const ref = collection(db, "projects", projId, "commentaires");
    const unsub = onSnapshot(ref, (snap) => {
      const data = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      // Tri : top-level chronologique (plus ancien en haut), replies aussi.
      // Le composant d'affichage gère ensuite l'arborescence parent/enfants.
      data.sort((a, b) => (a.dateCreation || 0) - (b.dateCreation || 0));
      setCommentaires(data);
      setLoading(false);
    }, (err) => {
      console.warn("useProjectCommentaires error:", err.message);
      setLoading(false);
    });
    return () => unsub();
  }, [projId]);

  const ajouterCommentaire = useCallback(async (data) => {
    exigerEcritureInvite();
    if (!projId) return null;
    const id = newId();
    const now = Date.now();
    await setDoc(doc(db, "projects", projId, "commentaires", id), {
      id, projId,
      parentId: data.parentId || null,
      auteurId: data.auteurId || null,
      auteurNom: data.auteurNom || "",
      auteurFonction: data.auteurFonction || "",
      auteurMutuelle: data.auteurMutuelle || "",
      auteurType: data.auteurType || "mutuelle",
      texte: (data.texte || "").trim(),
      pieceJointe: data.pieceJointe || null,
      visibilite: data.visibilite || "prive",
      statut: data.statut || "en_attente",
      dateCreation: now,
      dateMaj: now,
      validePar: null,
      motifRejet: null,
    });
    return id;
  }, [projId]);

  const validerCommentaire = useCallback(async (commId, adminUserId, options = {}) => {
    exigerEcritureInvite();
    if (!projId || !commId) return;
    await setDoc(doc(db, "projects", projId, "commentaires", commId), {
      statut: "publie",
      visibilite: options.visibilite || "public",
      validePar: adminUserId || null,
      motifRejet: null,
      dateMaj: Date.now(),
    }, { merge: true });
  }, [projId]);

  const refuserCommentaire = useCallback(async (commId, adminUserId, motif) => {
    exigerEcritureInvite();
    if (!projId || !commId) return;
    await setDoc(doc(db, "projects", projId, "commentaires", commId), {
      statut: "rejete",
      validePar: adminUserId || null,
      motifRejet: motif || "",
      dateMaj: Date.now(),
    }, { merge: true });
  }, [projId]);

  const toggleVisibiliteCommentaire = useCallback(async (commId, adminUserId, nouvelle) => {
    exigerEcritureInvite();
    if (!projId || !commId) return;
    await setDoc(doc(db, "projects", projId, "commentaires", commId), {
      visibilite: nouvelle,
      validePar: adminUserId || null,
      dateMaj: Date.now(),
    }, { merge: true });
  }, [projId]);

  const supprimerCommentaire = useCallback(async (commId) => {
    exigerEcritureInvite();
    if (!projId || !commId) return;
    await deleteDoc(doc(db, "projects", projId, "commentaires", commId));
  }, [projId]);

  return {
    commentaires, loading,
    ajouterCommentaire, validerCommentaire, refuserCommentaire,
    toggleVisibiliteCommentaire, supprimerCommentaire,
  };
}

// ── ATHOS — Présentations (outil "Préparer une présentation") ───────────────
//
// Collection presentations/{id}. Stocke les brouillons et présentations
// finalisées exportées en .pptx. Chaque doc :
// {
//   id, ownerId, ownerScope,        // "enio" (admin) ou "mutuelle:<id>"
//   titre, sousTitre, dateRendu, destinataire,
//   templateId,                     // codir_mutuelle | ca_enio | bureau |
//                                   //   reunion_technique | libre
//   sections: [...],                // structure des slides choisies
//   projetsSelectionnes: [...],     // {projId, options:{...}}
//   contenusActives: {...},         // {dataviz:bool, panorama:bool, ...}
//   draft: bool,
//   dateCreation, dateMaj,
// }
export function usePresentations(aramisSession) {
  const [presentations, setPresentations] = useState([]);
  const [loading, setLoading] = useState(true);
  const userId = aramisSession?.id;
  useEffect(() => {
    if (!userId) { setPresentations([]); setLoading(false); return; }
    const ref = collection(db, "presentations");
    const unsub = onSnapshot(ref, (snap) => {
      const data = snap.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .filter(p => p.ownerId === userId)   // sécurité applicative : seulement les miens
        .sort((a, b) => (b.dateMaj || 0) - (a.dateMaj || 0));
      setPresentations(data);
      setLoading(false);
    }, (err) => {
      console.warn("usePresentations error:", err.message);
      setLoading(false);
    });
    return () => unsub();
  }, [userId]);

  const sauvegarderPresentation = useCallback(async (data) => {
    exigerEcritureInvite();
    const id = data.id || newId();
    const now = Date.now();
    await setDoc(doc(db, "presentations", id), {
      ...data,
      id,
      ownerId: data.ownerId || userId,
      dateMaj: now,
      dateCreation: data.dateCreation || now,
    });
    return id;
  }, [userId]);

  const supprimerPresentation = useCallback(async (id) => {
    exigerEcritureInvite();
    await deleteDoc(doc(db, "presentations", id));
  }, []);

  return { presentations, loading, sauvegarderPresentation, supprimerPresentation };
}

// ── ATHOS — Réunions ──────────────────────────────────────────────────────────
//
// Structure d'un document meeting :
// {
//   id: string,
//   intitule: string,                     // ex. "GT Fraude documentaire"
//   date: number,                         // timestamp ms
//   domaine: string,                      // optionnel
//   pole: string,                         // optionnel
//   projectId: string | null,             // lien optionnel vers un projet
//   format: "presentiel" | "distanciel" | "hybride",
//   notes: string,
//   presents: {                           // clé = id mutuelle
//     [mutuellleId]: string[]             // liste des noms des participants
//   },
//   dateCreation: number,
//   dateMaj: number,
// }

export function useMeetings(enabled = true) {
  const [meetings, setMeetings] = useState([]);
  const [loading,  setLoading]  = useState(true);
  useEffect(() => {
    if (!enabled) { setMeetings(prev => (prev.length ? [] : prev)); return undefined; }
    const unsub = onSnapshot(MEETINGS_COL, (snap) => {
      const data = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      data.sort((a, b) => (b.date || 0) - (a.date || 0)); // plus récent en premier
      setMeetings(data); setLoading(false);
    }, (err) => {
      console.warn("useMeetings error:", err.message); setLoading(false);
    });
    return () => unsub();
  }, [enabled]);
  const saveMeeting = useCallback(async (meeting) => {
    exigerEcritureInvite();
    const id = meeting.id || newId();
    mesurerEcriture("saveMeeting");
    await setDoc(doc(db, "meetings", id), {
      ...meeting,
      id,
      dateMaj: Date.now(),
      dateCreation: meeting.dateCreation || Date.now(),
    });
  }, []);
  const deleteMeeting = useCallback(async (id) => {
    exigerEcritureInvite();
    await deleteDoc(doc(db, "meetings", id));
  }, []);
  return { meetings, saveMeeting, deleteMeeting, loading };
}

// ── ATHOS — Membres (comptes nominatifs des mutuelles) ───────────────────────
//
// Structure d'un document member :
// {
//   id: string,
//   prenom: string,
//   nom: string,
//   email: string,
//   mutuellleId: string,                  // id de la mutuelle (memberLists)
//   role: "dg" | "delegue" | "autre",
//   delegueId: string | null,             // lien optionnel vers delegates
//   passwordHash: string,                 // SHA-256 du mot de passe
//   totp: { secret: string, verified: boolean } | null,
//   firstLogin: boolean,                  // true = doit changer son mot de passe
//   actif: boolean,
//   dateCreation: number,
//   dateMaj: number,
//   creePar: string,                      // id admin créateur
// }

export function useMembers(enabled = true) {
  const [members,  setMembers]  = useState([]);
  const [loading,  setLoading]  = useState(true);
  useEffect(() => {
    if (!enabled) { setMembers(prev => (prev.length ? [] : prev)); return undefined; }
    const unsub = onSnapshot(MEMBERS_COL, (snap) => {
      const data = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      data.sort((a, b) =>
        (a.nom || "").localeCompare(b.nom || "", "fr") ||
        (a.prenom || "").localeCompare(b.prenom || "", "fr")
      );
      setMembers(data); setLoading(false);
    }, (err) => {
      console.warn("useMembers error:", err.message); setLoading(false);
    });
    return () => unsub();
  }, [enabled]);
  const saveMember = useCallback(async (member) => {
    exigerEcritureInvite();
    const id = member.id || newId();
    mesurerEcriture("saveMember");
    await setDoc(doc(db, "members", id), {
      ...member,
      id,
      dateMaj: Date.now(),
      dateCreation: member.dateCreation || Date.now(),
      actif: member.actif !== undefined ? member.actif : true,
      firstLogin: member.firstLogin !== undefined ? member.firstLogin : true,
    });
  }, []);
  const deleteMember = useCallback(async (id) => {
    exigerEcritureInvite();
    await deleteDoc(doc(db, "members", id));
  }, []);
  // Récupère les membres d'une seule mutuelle (filtrage côté client)
  const getMembersByMutuelle = useCallback((mutuellleId) => {
    return members.filter(m => m.mutuellleId === mutuellleId && m.actif);
  }, [members]);
  return { members, saveMember, deleteMember, getMembersByMutuelle, loading };
}

export function needsAnnuaireVerif(delegate) {
  if (!delegate.lastVerifAnnuaire) return true;
  const SIX_MOIS = 6 * 30 * 24 * 3600 * 1000;
  return Date.now() - delegate.lastVerifAnnuaire > SIX_MOIS;
}

export function genTempPassword() {
  const chars = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
  return Array.from({length:8}, () => chars[Math.floor(Math.random()*chars.length)]).join("");
}


// ── HERMÈS — Réunions Aramis ─────────────────────────────────────────────────
export function useReunions(enabled = true) {
  const [reunions, setReunions] = useState([]);
  useEffect(() => {
    if (!enabled) { setReunions(prev => (prev.length ? [] : prev)); return undefined; }
    const unsub = onSnapshot(collection(db, "reunions"), (snap) => {
      const data = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      data.sort((a, b) => (b.dateISO || "").localeCompare(a.dateISO || ""));
      setReunions(data);
    }, (err) => console.warn("useReunions error:", err.message));
    return () => unsub();
  }, [enabled]);

  const saveReunion = useCallback(async (reunion) => {
    exigerEcritureInvite();
    const id = reunion.id || newId();
    // {merge: true} CRITIQUE : sans ça, un setDoc depuis le modal d'édition
    // clobber les champs écrits par la Cloud Function (outlookEventId,
    // teamsJoinUrl, convivialEventId, etc.). Résultat : au 2e appel de
    // creerReunion, le backend croit être en mode CREATE et POSTe un nouvel
    // événement Outlook au lieu de PATCHer l'existant.
    mesurerEcriture("saveReunion");
    // Par le serveur (fermeture des règles de `reunions`, 20/10/2026) : même
    // fusion qu'avant, mais seuls les champs qui changent sont écrits, jamais
    // un champ des fonctions (Outlook, Teams, Noota) ; création par un
    // administrateur, modification par l'équipe ou l'organisateur.
    await reunionEcrire("enregistrer", { reunion: { ...reunion, id } });
    return id;
  }, []);

  const updateInviteStatut = useCallback(async (reunionId, inviteId, statut, repas) => {
    exigerEcritureInvite();
    // Par le serveur (20/10/2026) : la réunion est relue en transaction, seule
    // cette ligne change ; l'invité répond pour SA ligne, l'équipe et
    // l'organisateur pour toutes.
    const rep = await reunionEcrire("repondre", { reunionId, inviteId, statut, ...(repas !== undefined ? { repas } : {}) });
    const r = { themisSessionId: rep.themisSessionId || null };
    const oldInvite = rep.email ? { email: rep.email } : null;
    // MÊME RÉPONSE DES DEUX CÔTÉS (demande Pierre du 16/09/2026) : si cette
    // réunion est liée à une séance d'instance, la réponse saisie ici devient
    // aussi la réponse de la séance (statut du participant, quorum). Le
    // serveur vérifie le droit : l'équipe, ou la personne pour elle-même.
    // Non bloquant : un report refusé ne doit pas annuler la réponse.
    if (r.themisSessionId && oldInvite && oldInvite.email) {
      const versSeance = { confirme: "presentiel", present: "presentiel", accepte: "presentiel",
        visio: "visio", distanciel: "visio",
        excuse: "excuse", absent: "excuse", refuse: "excuse",
        provisoire: "sans_reponse", en_attente: "sans_reponse" }[String(statut || "").toLowerCase()];
      if (versSeance) {
        try {
          const { getFunctions, httpsCallable } = await import("firebase/functions");
          const fn = httpsCallable(getFunctions(undefined, REGION_FRANCFORT), "themisPresenceSaisieEquipe");
          await fn({ sessionId: r.themisSessionId, email: oldInvite.email, statut: versSeance });
        } catch (e) { console.warn("[réunion→séance] report:", e?.message || e); }
      }
    }
  }, []);

  return { reunions, saveReunion, updateInviteStatut };
}

// ============================================================================
// PREUVE DE CONNEXION (lot L1a « Engagement assaini », 27/09/2026)
// ============================================================================
// Points, badges et classements sont RETIRÉS : plus de barème, plus
// d'historique de points, plus de tri par mérite. Seule survit la TRACE DE
// CONNEXION gamification/{delegateId}.lastLogin, preuve de compte actif lue
// par src/delegateStatut.js (« Gestion des membres », équipe d'une mutuelle,
// adoption, historiques). Leçon du 27/08 : sans elle, tout l'annuaire repasse
// en orange et les filtres proposent d'inviter des membres actifs.
//   • Écriture : noterConnexion, appelée par loginAramis (App.jsx) exactement
//     comme l'ancien point quotidien de connexion : au plus une fois par jour
//     (UTC), jamais pour une session invitée. Plus rien d'autre n'écrit ici.
//   • Lecture : useDernieresConnexions → [{ id, userId, lastLogin }], même
//     garde qu'avant (liste vide tant que la collection n'a pas répondu : les
//     écrans attendent une preuve avant d'offrir une invitation, cf.
//     preuvesPretes).
// Lot L1b (serveur) : la trace passera dans connexions/{id}, écrite par les
// Cloud Functions de connexion ; ce bloc deviendra un repli transitoire. Le
// nom de la collection reste « gamification » jusque-là : le changer couperait
// la lecture des traces existantes.
const COLLECTION_TRACES = "gamification";

/** Jour UTC (AAAA-MM-JJ) d'une date en ms, ou "" : même découpage que l'ancien
 *  point quotidien de connexion. */
function jourUTC(ms) {
  const n = Number(ms);
  if (!Number.isFinite(n) || n <= 0) return "";
  return new Date(n).toISOString().slice(0, 10);
}

/** Écrit la trace de connexion d'une personne ({ lastLogin } en fusion), AU
 *  PLUS une fois par jour : `loginAramis` sert aussi à rafraîchir la session
 *  après un enregistrement de profil, et réécrire la date à chaque appel ferait
 *  dire à « dernière connexion » la date de la dernière modification de profil.
 *  Ne crédite AUCUN point. Rend true si la trace a été écrite. */
export async function noterConnexion(userId, maintenant = Date.now()) {
  if (!userId) return false;
  if (_isInviteSessionLS()) return false;   // invité : aucune écriture
  const ref = doc(db, COLLECTION_TRACES, String(userId));
  const snap = await getDoc(ref);
  const precedente = snap.exists() ? (snap.data() || {}).lastLogin : null;
  if (jourUTC(precedente) === jourUTC(maintenant)) return false;
  await setDoc(ref, { lastLogin: maintenant }, { merge: true });
  return true;
}

/** Documents de la collection des traces → [{ id, userId, lastLogin }]. Tous
 *  les documents sont gardés, même sans date (`preuvesPretes` compte la liste,
 *  comme avant). Aucun autre champ ne sort d'ici, ni points ni historique :
 *  aucun écran ne peut plus en tirer un classement. */
export function versTracesConnexion(docs) {
  return (docs || []).map(d => {
    const data = (typeof d.data === "function" ? d.data() : d.data) || {};
    return { id: d.id, userId: d.id, lastLogin: data.lastLogin };
  });
}

/** Traces de connexion de tous les comptes, en direct. Remplace l'ancien
 *  « classement » : même collection, même garde (`enabled`), plus aucun tri. */
export function useDernieresConnexions(enabled = true) {
  const [traces, setTraces] = useState([]);
  useEffect(() => {
    if (!enabled) { setTraces(prev => (prev.length ? [] : prev)); return undefined; }
    const unsub = onSnapshot(collection(db, COLLECTION_TRACES),
      (snap) => setTraces(versTracesConnexion(snap.docs)), () => {});
    return () => unsub();
  }, [enabled]);
  return { traces };
}

// ── HERMÈS — Externes (annuaire invités externes) ────────────────────────────
export function useExternesReunion(enabled = true) {
  const [externes, setExternes] = useState([]);
  useEffect(() => {
    if (!enabled) { setExternes(prev => (prev.length ? [] : prev)); return undefined; }
    const unsub = onSnapshot(collection(db, "externesReunion"), (snap) => {
      setExternes(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    }, (err) => console.warn("useExternesReunion error:", err.message));
    return () => unsub();
  }, [enabled]);

  const saveExterne = useCallback(async (ext) => {
    exigerEcritureInvite();
    const id = ext.id || newId();
    mesurerEcriture("saveExterne");
    await setDoc(doc(db, "externesReunion", id), { ...ext, id, dateMaj: Date.now() });
    return id;
  }, []);

  return { externes, saveExterne };
}

// ── Dataviz collaborative ────────────────────────────────────────────────────

/** Sanitize mutuelle name pour un ID Firestore stable */
export function sanitizeMut(name) {
  return (name || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-zA-Z0-9]/g, "_").replace(/_+/g, "_").replace(/^_|_$/g, "");
}

/** Exercices (années) — CRUD */
export function useDatavizExercices() {
  const [exercices, setExercices] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    const unsub = onSnapshot(collection(db, "datavizExercices"), (snap) => {
      const data = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      data.sort((a, b) => (b.annee || 0) - (a.annee || 0));
      setExercices(data); setLoading(false);
    }, (err) => { console.warn("useDatavizExercices error:", err.message); setLoading(false); });
    return () => unsub();
  }, []);
  const saveExercice = useCallback(async (ex) => {
    exigerEcritureInvite();
    const id = ex.id || String(ex.annee || newId());
    mesurerEcriture("saveExercice");
    await setDoc(doc(db, "datavizExercices", id), { ...ex, id, dateMaj: Date.now(), dateCreation: ex.dateCreation || Date.now() });
  }, []);
  const deleteExercice = useCallback(async (id) => {
    exigerEcritureInvite(); await deleteDoc(doc(db, "datavizExercices", id)); }, []);
  return { exercices, saveExercice, deleteExercice, loading };
}

/** Métriques pour un exercice donné */
export function useDatavizMetrics() {
  const [metrics, setMetrics] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    const unsub = onSnapshot(collection(db, "datavizMetrics"), (snap) => {
      const data = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      data.sort((a, b) => {
        if (a.section !== b.section) return (a.section || "").localeCompare(b.section || "", "fr");
        return (a.ordre || 0) - (b.ordre || 0);
      });
      setMetrics(data); setLoading(false);
    }, (err) => { console.warn("useDatavizMetrics error:", err.message); setLoading(false); });
    return () => unsub();
  }, []);
  const saveMetric = useCallback(async (m) => {
    exigerEcritureInvite();
    const id = m.id || newId();
    mesurerEcriture("saveMetric");
    await setDoc(doc(db, "datavizMetrics", id), { ...m, id, dateMaj: Date.now(), dateCreation: m.dateCreation || Date.now() });
  }, []);
  const deleteMetric = useCallback(async (id) => {
    exigerEcritureInvite(); await deleteDoc(doc(db, "datavizMetrics", id)); }, []);
  return { metrics, saveMetric, deleteMetric, loading };
}

/** Entrées de données (une par mutuelle par exercice) */
export function useDatavizEntries() {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    const unsub = onSnapshot(collection(db, "datavizEntries"), (snap) => {
      setEntries(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setLoading(false);
    }, (err) => { console.warn("useDatavizEntries error:", err.message); setLoading(false); });
    return () => unsub();
  }, []);
  const saveEntry = useCallback(async (entry) => {
    exigerEcritureInvite();
    const id = entry.id || (entry.exerciceId + "_" + sanitizeMut(entry.mutuelle));
    // SÉCURITÉ : protection anti-dégradation d'entrée validée
    // Si l'entrée existante est "valide", on ne peut pas revenir à "brouillon"/"soumis"
    // ni écraser les valeurs officielles sans passer par correction_demandee.
    // L'admin peut explicitement bypass cette protection avec `_adminOverride: true`
    // (utilisé par le bouton "Dé-valider" dans le suivi des saisies).
    const adminOverride = entry._adminOverride === true;
    try {
      const { getDoc } = await import("firebase/firestore");
      const existingSnap = await getDoc(doc(db, "datavizEntries", id));
      if (existingSnap.exists()) {
        const existing = existingSnap.data();
        if (existing.statut === "valide" && !adminOverride) {
          const newStatut = entry.statut || "brouillon";
          // Autorisés depuis valide : valide (update admin), correction_demandee (user), rejete (admin)
          if (!["valide", "correction_demandee", "rejete"].includes(newStatut)) {
            console.warn(`[saveEntry] Blocage : tentative de dégrader une entrée validée (${existing.mutuelle}) de "valide" vers "${newStatut}". Écrasement refusé.`);
            // On préserve statut et valeurs officielles mais on accepte le draft
            entry = {
              ...existing,
              valeursDraft: entry.valeurs || {}, // draft temporaire (non officiel)
              visibiliteOverridesDraft: entry.visibiliteOverrides || {},
              draftPar: entry.saisiPar,
              draftParNom: entry.saisiParNom,
              draftLe: Date.now(),
            };
          }
        }
      }
    } catch(e) { console.warn("saveEntry protection:", e.message); }
    // On retire le flag d'override avant écriture (pas besoin de le persister)
    const { _adminOverride: _, ...toWrite } = entry;
    mesurerEcriture("saveEntry");
    await setDoc(doc(db, "datavizEntries", id), { ...toWrite, id, dateMaj: Date.now(), dateCreation: toWrite.dateCreation || Date.now() });
  }, []);
  return { entries, saveEntry, loading };
}

// Propositions de saisie issues de la relance dataviz augmentée (1 par auteur,
// doc id = token). Lecture temps réel + marquage « traité » côté admin.
export function useDatavizPropositions() {
  const [propositions, setPropositions] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    const unsub = onSnapshot(collection(db, "datavizPropositions"), (snap) => {
      setPropositions(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setLoading(false);
    }, (err) => { console.warn("useDatavizPropositions error:", err.message); setLoading(false); });
    return () => unsub();
  }, []);
  // Marque des propositions comme traitées (arbitrage appliqué).
  const markPropositionsApplied = useCallback(async (tokens, extra = {}) => {
    exigerEcritureInvite();
    await Promise.all((tokens || []).map(t =>
      setDoc(doc(db, "datavizPropositions", t), { statut: "applique", traiteLe: Date.now(), ...extra }, { merge: true })
        .catch(e => console.warn("markPropositionsApplied:", e.message))
    ));
  }, []);
  return { propositions, loading, markPropositionsApplied };
}

// ============================================================================
// useChat — Messagerie utilisateur ↔ utilisateur (1-to-1 et groupes)
// ============================================================================
// Collections :
//   chatConversations/{convId}                 → métadonnées (members, dernier
//                                                 message, compteurs non-lus)
//   chatConversations/{convId}/messages/{id}   → messages individuels
//
// Le filtrage par membre est fait côté client (chargement complet puis filter)
// pour éviter la création d'un index composite Firestore. OK tant que le volume
// reste raisonnable (typiquement < 500 conversations totales).
//
// Pattern de non-lus : à chaque envoi de message, la couche client incrémente
// unread[memberId] pour tous les membres SAUF l'auteur. markAsRead remet à 0
// le compteur du user courant.
// ============================================================================
export function useChat(currentUserId) {
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!currentUserId) { setConversations([]); setLoading(false); return; }
    const unsub = onSnapshot(collection(db, "chatConversations"), (snap) => {
      const all = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      // Filtrage client : ne garder que les conversations où l'user est membre
      // et non archivées par admin.
      const mine = all
        .filter(c => Array.isArray(c.members) && c.members.includes(currentUserId))
        .filter(c => c.archived !== true)
        .sort((a, b) => (b.lastMessageAt || 0) - (a.lastMessageAt || 0));
      setConversations(mine);
      setLoading(false);
    }, (err) => { console.warn("useChat error:", err.message); setLoading(false); });
    return () => unsub();
  }, [currentUserId]);

  // Cherche une conversation directe existante entre 2 users (idempotent).
  // Retourne le conv ou null. Filtre client (pas de query Firestore complexe).
  const findDirectConversation = useCallback((otherUserId) => {
    if (!currentUserId || !otherUserId || currentUserId === otherUserId) return null;
    return conversations.find(c =>
      c.type === "direct" &&
      Array.isArray(c.members) && c.members.length === 2 &&
      c.members.includes(currentUserId) &&
      c.members.includes(otherUserId)
    ) || null;
  }, [conversations, currentUserId]);

  // Crée une conversation (direct ou groupe). Pour direct, vérifie d'abord
  // qu'aucune ne préexiste — retourne l'existante le cas échéant.
  // currentUser = { id, prenom, nom, mutuelle } ; members = [{id,prenom,nom,mutuelle}, ...]
  const createConversation = useCallback(async ({ type, members, title, currentUser }) => {
    exigerEcritureInvite();
    if (!currentUser?.id) throw new Error("currentUser.id requis");
    if (!Array.isArray(members) || members.length === 0) throw new Error("members requis");
    // Inclure currentUser dans les membres s'il manque
    const allMembers = [currentUser, ...members.filter(m => m.id !== currentUser.id)];
    if (type === "direct") {
      if (allMembers.length !== 2) throw new Error("Une conversation directe doit avoir exactement 2 membres");
      const existing = findDirectConversation(allMembers.find(m => m.id !== currentUser.id).id);
      if (existing) return existing;
    }
    const membersInfo = {};
    const unread = {};
    for (const m of allMembers) {
      membersInfo[m.id] = {
        prenom: m.prenom || "", nom: m.nom || "", mutuelle: m.mutuelle || "",
      };
      unread[m.id] = 0;
    }
    const newId = Math.random().toString(36).slice(2, 11);
    const now = Date.now();
    const docData = {
      id: newId,
      type: type === "group" ? "group" : "direct",
      members: allMembers.map(m => m.id),
      membersInfo,
      title: title || null,
      createdBy: currentUser.id,
      createdAt: now,
      lastMessage: null,
      lastMessageAt: now,
      unread,
      archived: false,
    };
    await setDoc(doc(db, "chatConversations", newId), docData);
    return docData;
  }, [findDirectConversation]);

  // Envoie un message dans une conversation existante.
  // currentUser = { id, prenom, nom, mutuelle }
  const sendMessage = useCallback(async (convId, text, currentUser) => {
    exigerEcritureInvite();
    if (!convId || !text || !text.trim() || !currentUser?.id) return;
    const { addDoc, collection: coll, getDoc, updateDoc: upd } = await import("firebase/firestore");
    const cleanText = text.trim().slice(0, 5000); // garde-fou taille
    const auteurNom = ((currentUser.prenom||"") + " " + (currentUser.nom||"")).trim();
    const now = Date.now();
    // Append message dans la sous-collection
    await addDoc(coll(db, "chatConversations", convId, "messages"), {
      auteurId: currentUser.id,
      auteurNom,
      auteurMutuelle: currentUser.mutuelle || "",
      text: cleanText,
      ts: now,
    });
    // Mise à jour métadonnées conv : lastMessage + incrément non-lus
    const convRef = doc(db, "chatConversations", convId);
    const snap = await getDoc(convRef);
    if (!snap.exists()) return;
    const conv = snap.data();
    const newUnread = { ...(conv.unread || {}) };
    for (const memberId of (conv.members || [])) {
      if (memberId !== currentUser.id) {
        newUnread[memberId] = (newUnread[memberId] || 0) + 1;
      }
    }
    await upd(convRef, {
      lastMessage: { text: cleanText, auteurId: currentUser.id, auteurNom, ts: now },
      lastMessageAt: now,
      unread: newUnread,
    });
  }, []);

  // Remet à 0 le compteur de non-lus pour l'user courant (à l'ouverture d'une conv).
  const markAsRead = useCallback(async (convId) => {
    if (!convId || !currentUserId) return;
    try {
      const { updateDoc: upd, getDoc } = await import("firebase/firestore");
      const convRef = doc(db, "chatConversations", convId);
      const snap = await getDoc(convRef);
      if (!snap.exists()) return;
      const conv = snap.data();
      if ((conv.unread || {})[currentUserId] === 0) return; // pas de changement
      const newUnread = { ...(conv.unread || {}), [currentUserId]: 0 };
      await upd(convRef, { unread: newUnread });
    } catch (e) { console.warn("[chat] markAsRead error:", e.message); }
  }, [currentUserId]);

  // Compteur agrégé de non-lus pour le badge global
  const totalUnread = conversations.reduce(
    (sum, c) => sum + ((c.unread || {})[currentUserId] || 0), 0);

  return { conversations, sendMessage, createConversation, markAsRead,
    findDirectConversation, totalUnread, loading };
}

// Hook séparé pour écouter les messages d'UNE conversation (sous-collection).
// On ne charge pas tous les messages en permanence : seulement quand le thread
// est ouvert (composant FloatingChat mount/unmount le hook).
export function useChatMessages(convId) {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    if (!convId) { setMessages([]); setLoading(false); return; }
    const unsub = onSnapshot(collection(db, "chatConversations", convId, "messages"),
      (snap) => {
        const list = snap.docs
          .map(d => ({ id: d.id, ...d.data() }))
          .sort((a, b) => (a.ts || 0) - (b.ts || 0));
        setMessages(list);
        setLoading(false);
      },
      (err) => { console.warn("useChatMessages error:", err.message); setLoading(false); });
    return () => unsub();
  }, [convId]);
  return { messages, loading };
}
